<template>
  <div class="d-flex trail-grid__controls">
    <div class=" col-md-12 align-self-center py-1">
      <v-form
        ref="trailDateForm"
        v-model="valid"
        lazy-validation
        class="d-flex align-center"
      >
        <v-row no-gutters>
          <v-col align-self="center">
            <div class="d-flex align-center justify-center">
              <div>
                <SvgIcon width="50" height="50" iconType="icon_trails" />
              </div>
              <div class="">
                <div class="h4 mb-0 secondary--text font-weight-bold">
                  {{ $t('live_tracking.map.trail_grid.labels.card_title') }}
                </div>
                <div class="mb-0 tertiary--text">{{ getThing.name }}</div>
              </div>
            </div>
          </v-col>
          <v-divider vertical class="mr-2"></v-divider>
          <v-col align-self="center">
            <v-select
              dense
              v-model="selectDateRange"
              :items="items"
              item-text="text"
              item-value="value"
              :label="
                $t('live_tracking.map.trail_grid.labels.label_date_range')
              "
              color="tertiary"
              item-color="tertiary"
              @change="changeRange"
              return-object
              hide-details
              class="mr-4 tertiary--text align-center"
              prepend-icon="mdi-calendar-clock"
            />
          </v-col>
          <v-col align-self="center">
            <v-menu
              v-model="menuDateStart"
              :close-on-content-click="false"
              :return-value.sync="dateStartComputed"
              offset-y
              :nudge-right="32"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dense
                  v-model="dateStartComputed"
                  :label="
                    $t('live_tracking.map.trail_grid.labels.label_date_start')
                  "
                  prepend-icon="mdi-calendar"
                  readonly
                  :disabled="pickerDisabled"
                  v-bind="attrs"
                  hide-details="auto"
                  :rules="rules"
                  v-on="on"
                  class="mr-4 align-center"
                  color="tertiary"
                  :error-messages="errorMessage"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                :min="minDate"
                :max="endDate"
                color="tertiary"
                no-title
                @input="menuDateStart = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col align-self="center">
            <v-menu
              ref="menuTimeStart"
              v-model="menuTimeStart"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="startTime"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dense
                  v-model="startTime"
                  :label="
                    $t('live_tracking.map.trail_grid.labels.label_time_start')
                  "
                  prepend-icon="mdi-clock-time-four-outline"
                  readonly
                  :disabled="pickerDisabled"
                  v-bind="attrs"
                  hide-details="auto"
                  :rules="rules"
                  :error-messages="errorMessage"
                  color="tertiary"
                  v-on="on"
                  class="mr-4 align-center"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="menuTimeStart"
                v-model="startTime"
                color="tertiary"
                no-title
                @click:minute="$refs.menuTimeStart.save(startTime)"
              ></v-time-picker>
            </v-menu>
          </v-col>
          <v-col align-self="center">
            <v-menu
              v-model="menuDateEnd"
              :close-on-content-click="false"
              :return-value.sync="dateEndComputed"
              offset-y
              :nudge-right="32"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dense
                  v-model="dateEndComputed"
                  :label="
                    $t('live_tracking.map.trail_grid.labels.label_date_end')
                  "
                  prepend-icon="mdi-calendar"
                  readonly
                  :disabled="pickerDisabled"
                  :rules="rules"
                  :error-messages="errorMessage"
                  hide-details="auto"
                  v-bind="attrs"
                  color="tertiary"
                  v-on="on"
                  class="mr-4 align-center"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                :min="minDate"
                :max="today"
                color="tertiary"
                no-title
                @input="menuDateEnd = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col align-self="center">
            <v-menu
              ref="menuTimeEnd"
              v-model="menuTimeEnd"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="endTime"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dense
                  v-model="endTime"
                  :label="
                    $t('live_tracking.map.trail_grid.labels.label_time_end')
                  "
                  prepend-icon="mdi-clock-time-four-outline"
                  readonly
                  :rules="rules"
                  :error-messages="errorMessage"
                  :disabled="pickerDisabled"
                  v-bind="attrs"
                  class="mr-4 align-center"
                  color="tertiary"
                  v-on="on"
                  hide-details="auto"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="menuTimeEnd"
                v-model="endTime"
                color="tertiary"
                no-title
                @click:minute="$refs.menuTimeEnd.save(endTime)"
              ></v-time-picker>
            </v-menu>
          </v-col>
          <v-col align-self="center">
            <div>
              <span class="tertiary--text text-caption">{{
                $t('live_tracking.map.trail_grid.labels.show_events')
              }}</span>
              <v-switch
                hide-details="auto"
                v-model="showEvents"
                @change="toggleEventsColumn"
                class="mt-0"
              />
            </div>
          </v-col>
          <v-col align-self="center" class="text-center">
            <div class="d-flex justify-space-between align-center">
              <v-btn
                rounded
                v-bind="btnSize"
                :disabled="loadingDataTable"
                color="tertiary"
                class="text-caption white--text"
                @click="submitDateForm"
              >
                {{
                  $t(
                    'live_tracking.map.trail_grid.labels.label_consolidate_button'
                  )
                }}
              </v-btn>
              <v-menu
                :value="showMenuExport"
                offset-y
                nudge-bottom="12"
                :close-on-content-click="false"
                rounded-xl
                content-class="white menu__export pa-1"
              >
                <template v-slot:activator="{ on, attrs, value }">
                  <v-btn
                    :class="[value ? '' : 'btn__activator-not-focus']"
                    icon
                    v-bind="attrs"
                    :disabled="loadingDataTable"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <div>
                  <v-btn :loading="loadingExport"  >
                    <SvgIcon
                      class="secondary--text mr-1 align-self-baseline"
                      iconType="icon_document_download"
                    />
                    <span class="text-body-2 text-capitalize">{{
                      $t('live_tracking.map.trail_grid.labels.export_to_excel')
                    }}</span>
                  </v-btn>
                </div>
              </v-menu>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </div>
  </div>
</template>

<script>
import {
  SvgIcon,
  MIN_SPEED_THING_VEHICLE,
  CLICKGO_MAX_BUNDLE_VALUE
} from '@leapsight/styleguide'
import { mapGetters, mapState, mapActions } from 'vuex'
import { i18n } from '@leapsight/portal'
import {
  GlobalHelpers,
  ThingsHelpers,
  TrailsHelpers
} from '@/helpers/mainExports'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
//import { saveAs } from 'file-saver'
//import ExcelJS from 'exceljs/dist/es5/exceljs.browser.js'
export default {
  name: 'TrailForm',
  components: {
    SvgIcon
  },

  data() {
    return {
      loadingButton: false,
      endTime: this.getHour(),
      endDate: dayjs()
        .toISOString()
        .substr(0, 10),
      errorMessage: '',

      items: [
        {
          value: 'custom',
          text: i18n.t(
            'live_tracking.map.trail_grid.labels.picker_range_custom'
          )
        },
        {
          value: 'today',
          text: i18n.t('live_tracking.map.trail_grid.labels.picker_range_today')
        },
        {
          value: 'lastTwentyFour',
          text: i18n.t(
            'live_tracking.map.trail_grid.labels.picker_range_last_twenty_four'
          )
        },
        {
          value: 'yesterday',
          text: i18n.t(
            'live_tracking.map.trail_grid.labels.picker_range_yesterday'
          )
        },
        {
          value: 'oneWeek',
          text: i18n.t(
            'live_tracking.map.trail_grid.labels.picker_range_one_week'
          )
        },
        {
          value: 'twoWeeks',
          text: i18n.t(
            'live_tracking.map.trail_grid.labels.picker_range_two_weeks'
          )
        },
        {
          value: 'threeWeeks',
          text: i18n.t(
            'live_tracking.map.trail_grid.labels.picker_range_three_weeks'
          )
        }
      ],
      menuDateStart: false,
      menuTimeStart: false,
      minDate: dayjs()
        .subtract(3, 'month')
        .toISOString(),
      today: dayjs().toISOString(),
      loadingExport: false,
      menuDateEnd: false,
      menuTimeEnd: false,
      pickerDisabled: true,
      rules: [() => this.checkRules()],
      showMenuExport: false,
      selectDateRange: { text: 'Hoy', value: 'today' },
      startTime: '00:00',
      startDate: dayjs()
        .toISOString()
        .substr(0, 10) //día actual
    }
  },
  methods: {
    changeRange(item) {
      if (item.value != 'custom') {
        this.pickerDisabled = true
        switch (item.value) {
          //Hoy
          case 'today':
            this._setToday()
            break
          //1 Día
          case 'yesterday':
            this._setYesterday()
            break
          //1 Día
          case 'lastTwentyFour':
            this._setLastTwentyFour()
            break
          //1 Semana
          case 'oneWeek':
            this._setOneWeek()
            break
          //2 Semanas
          case 'twoWeeks':
            this._setTwoWeeks()
            break
          //3 Semanas
          case 'threeWeeks':
            this._setThreeWeeks()
            break

          default:
            break
        }
      } else this.pickerDisabled = false
    },
    checkRules() {
      const startDate = dayjs(this.startDate + ' ' + this.startTime).valueOf()
      const endDate = dayjs(this.endDate + ' ' + this.endTime).valueOf()
      //validamos si la fecha de inicio es menor que la de fin
      if (startDate <= endDate) {
        //2592000000 = equivalente a 1 mes
        if (endDate - startDate <= 2592000000) {
          this.errorMessage = ''
          return true
        } else {
          this.errorMessage = i18n.t(
            'live_tracking.map.trail_grid.rules.longer_date'
          )
          return false
        }
      } else {
        this.errorMessage = i18n.t(
          'live_tracking.map.trail_grid.rules.future_date'
        )
        return false
      }
    },
    currentItems: function(items) {
      if (items.length > 0) {
        const tempArr = []
        for (let i = 0; i < items.length; i++) {
          if (!items[i].hasOwnProperty('geoReference')) {
            //TempArr se queda con la referencia de posiciones sobre el array original items
            tempArr.push({
              index: i,
              latitude: items[i].location.coordinates[0],
              longitude: items[i].location.coordinates[1]
            })
          }
        }

        const chunkCurrentItems = GlobalHelpers.chunkArray(
          tempArr,
          CLICKGO_MAX_BUNDLE_VALUE
        )
        const forLoop = async () => {
          //Activar los loadings antes de procesar los bundles
          for (let index = 0; index < chunkCurrentItems.length; index++) {
            const element = chunkCurrentItems[index]
            try {
              const response = await GlobalHelpers.getBundleLocation(element)
              for (
                let indexResponse = 0;
                indexResponse < response.length;
                indexResponse++
              ) {
                const responseElement = response[indexResponse]
                //Para guardar en el state la georeferencia obtengo el id del objeto en cuestión
                const id = items[responseElement.index].id
                const payload = { id, geoReference: responseElement }

                this.setGeoReference(payload)
              }
            } catch (err) {
              //console.log(err) // TypeError: failed to fetch
            }
          }
        }
        forLoop()
      }
    },
    getPartialDistance(distance) {
      return TrailsHelpers.mtsToKms(distance)
    },
    getHeading(item) {
      if (item.speed >= MIN_SPEED_THING_VEHICLE) {
        const enHeading = ThingsHelpers.getThingHeading(item.heading)
        const esHeading = i18n.t(`general.labels.heading.short.${enHeading}`)
        return esHeading
      } else {
        return '-'
      }
    },

    getLocationDate(item) {
      const now = dayjs
        .utc(item.timestamp)
        .utcOffset(-3)
        .format('DD MMM YYYY H:mm:ss')
      return now
    },
    getHour() {
      dayjs.extend(utc)
      const now = dayjs
        .utc()
        .utcOffset(-3)
        .format('HH:mm')
      return now
    },
    getStatus(item, translateStatus = false) {
      const type = this.getThing.type
      let status = ThingsHelpers.getThingStatus(type, item)
      if (translateStatus)
        switch (status) {
          case 'moving':
            status = i18n.t(
              'live_tracking.map.thing_info.labels.label_thing_status_moving'
            )
            break
          case 'on':
            status = i18n.t(
              'live_tracking.map.thing_info.labels.label_thing_status_on'
            )
            break
          case 'stop':
            status = i18n.t(
              'live_tracking.map.thing_info.labels.label_thing_status_stop'
            )
            break
          case 'tow':
            status = i18n.t(
              'live_tracking.map.thing_info.labels.label_thing_status_tow'
            )
            break
        }
      return status
    },

    ...mapActions('trailState', [
      'toggleShowEvents',
      'setDataLoading',
      'setQueryTimestamp',
      'clearThingTrailLocations',
      'getThingTrail',
      'setGeoReference',
      'highLightMarker'
    ]),
    _setToday() {
      const now = dayjs()
      const d1 = now.toISOString().substr(0, 10)
      this.startDate = d1
      this.startTime = '00:00'
      this.endDate = new Date().toISOString().substr(0, 10)
      this.endTime = this.getHour()
    },
    _setYesterday() {
      const now = dayjs()
      const d1 = now
        .subtract('1', 'day')
        .toISOString()
        .substr(0, 10)
      this.startDate = d1
      this.startTime = '00:00'
      this.endDate = d1
      this.endTime = '23:59'
    },
    _setLastTwentyFour() {
      const now = dayjs()
      const d1 = now
        .subtract('1', 'day')
        .toISOString()
        .substr(0, 10)
      this.startDate = d1
      this.endDate = new Date().toISOString().substr(0, 10)
      this.endTime = this.getHour()
      this.startTime = this.endTime
    },
    _setOneWeek() {
      const now = dayjs()
      const d1 = now
        .subtract('1', 'week')
        .toISOString()
        .substr(0, 10)
      this.startDate = d1
      this.endDate = new Date().toISOString().substr(0, 10)
      this.endTime = this.getHour()
      this.startTime = this.endTime
    },
    _setTwoWeeks() {
      const now = dayjs()
      const d1 = now
        .subtract('2', 'week')
        .toISOString()
        .substr(0, 10)
      this.startDate = d1
      this.endDate = new Date().toISOString().substr(0, 10)
      this.endTime = this.getHour()
      this.startTime = this.endTime
    },
    _setThreeWeeks() {
      const now = dayjs()
      const d1 = now
        .subtract('3', 'week')
        .toISOString()
        .substr(0, 10)
      this.startDate = d1
      this.endDate = new Date().toISOString().substr(0, 10)
      this.endTime = this.getHour()
      this.startTime = this.endTime
    },
    submitDateForm() {
      if (this.$refs.trailDateForm.validate()) {
        //En el submit contemplamos el escenario donde el modal de recorrido historico no sufrió alteraciones
        //en sus controles de fecha y los valores en los inputs para los casos 'hoy' y 'últimas 24hs' difiere de la fecha y hora actual en la que se hizo submit
        const actualDate = dayjs()
          .toISOString()
          .substr(0, 10)
        switch (this.selectDateRange.value) {
          case 'today':
            if (this.getHour() !== this.endTime && actualDate == this.startDate)
              this.endTime = this.getHour()
            else {
              this.endTime = this.getHour()
              this.startDate = actualDate
            }
            break
          case 'lastTwentyFour':
            if (this.getHour() !== this.endTime || actualDate == this.startDate)
              this._setLastTwentyFour()
            break
          default:
            break
        }
        this.loadingButton = true
        this.setDataLoading(true)
        this.setQueryTimestamp(dayjs().valueOf())
        const payload = {
          from: dayjs(this.startDate + ' ' + this.startTime).valueOf(),
          to: dayjs(this.endDate + ' ' + this.endTime).valueOf()
        }

        this.clearThingTrailLocations()
        this.getThingTrail(payload)

        //Reset de scroll sobre la datatable
        const dataTableScroll = document.querySelector(
          '#recorrido .v-data-table__wrapper'
        )
        dataTableScroll.scrollTop = 0
        this.loadingButton = false
      }
    },
    processWithoutEvents(items) {
      const length = items.length
  //    const rowsForExcel = [] // Array de datos a devolver para exportación
      const arrToResolveGeoreference = [] // Array con elementos sin ubicación resuelta
      let location
      for (let index = 0; index < length; index++) {
        const element = items[index]
        //Se construye la estructura del objeto salvo la ubicación que se añade solo si se encuentra resuelta la georeferencia
        const { id, speed } = element
        const date = this.getLocationDate(element)
        const heading = this.getHeading(element)
        const latitude = element.location.coordinates[0]
        const longitude = element.location.coordinates[1]
        const partialDistance = this.getPartialDistance(element.partialDistance)
        const contact = element.contact_on ? 'On' : 'Off'
        const status = this.getStatus(element, true) //true solicita devolver traducido el status

        if (element.hasOwnProperty('geoReference')) {
          location = element.geoReference.formatted_address
        } else {
          // Si no se encuentra resuelta la georeferencia se guarda el indice del item, su latitud y longitud en el array para posterior proceso
          location = ''
          arrToResolveGeoreference.push({
            index,
            latitude,
            longitude
          })
        }
        //Array parcial con objetos a exportar
      /*  rowsForExcel.push({
          contact,
          date,
          heading,
          id,
          latitude,
          location,
          longitude,
          partialDistance,
          speed,
          status
        })*/
      }
      //return { rowsForExcel, arrToResolveGeoreference }
       return {  arrToResolveGeoreference }
    },
   /* processWithEvents(items) {
      const length = items.length
    //  const rowsForExcel = [] // Array de datos a devolver para exportación
      const arrToResolveGeoreference = [] // Array con elementos sin ubicación resuelta
      let location
      for (let index = 0; index < length; index++) {
        const element = items[index]
        //Se construye la estructura del objeto salvo la ubicación que se añade solo si se encuentra resuelta la georeferencia
        const { id, speed } = element
        const date = this.getLocationDate(element)
        const heading = this.getHeading(element)
        const latitude = element.location.coordinates[0]
        const longitude = element.location.coordinates[1]
        const partialDistance = this.getPartialDistance(element.partialDistance)
        const contact = element.contact_on ? 'On' : 'Off'
        const status = this.getStatus(element, true) //true solicita devolver traducido el status
        let events
        if (element.events_data !== null)
          events = TrailsHelpers.processEventsForExcel(element.events_data)
        else events = ''

        if (element.hasOwnProperty('geoReference')) {
          location = element.geoReference.formatted_address
        } else {
          // Si no se encuentra resuelta la georeferencia se guarda el indice del item, su latitud y longitud en el array para posterior proceso
          location = ''
          arrToResolveGeoreference.push({
            index,
            latitude,
            longitude
          })
        }
        //Array parcial con objetos a exportar
        rowsForExcel.push({
          contact,
          date,
          events,
          heading,
          id,
          latitude,
          location,
          longitude,
          partialDistance,
          speed,
          status
        })
      }
      return { rowsForExcel, arrToResolveGeoreference }
    },
    saveAsExcel() {
      this.showMenuExport = true
      this.loadingExport = true
      const wb = new ExcelJS.Workbook()
      const { name, domain } = this.getThing
      const localRequestDate = dayjs().format('DD/MM/YYYY hh:mm:ss')
      const formRequestParameters = wb.addWorksheet(
        i18n.t('live_tracking.map.trail_grid.export_excel.form_request_params')
      )
      const formRequestColumns = TrailsHelpers.getFormRequestColumns()
      formRequestParameters.columns = [...formRequestColumns]

      // Establezco el ancho de las columna basado en la cantidad de caracteres de su header
      formRequestParameters.columns.forEach(column => {
        column.width = column.header.length < 12 ? 12 : column.header.length
      })
      formRequestParameters.addRow({
        dateRange: `${this.dateStartComputed} ${this.startTime} | ${this.dateEndComputed} ${this.endTime}`,
        thingName: name,
        domain,
        localRequestDate,
        isConsolidate: 'false'
      })
      const ws = wb.addWorksheet(name)
      const wsColumns = TrailsHelpers.getLocationsColumns(this.showEvents)
      ws.columns = [...wsColumns]
      // Establezco el ancho de las columna basado en la cantidad de caracteres de su header
      ws.columns.forEach(column => {
        column.width = column.header.length < 12 ? 12 : column.header.length
      })

      let processedInformation
      const items = this.getThingTrailLocations
      if (this.showEvents) {
        processedInformation = this.processWithEvents(items)
      } else {
        processedInformation = this.processWithoutEvents(items)
      }

      // Se realiza un array chunk donde el tamaño de cada uno es el numero maximo de items que puede procesar el servicio de clickGo
      const chunkGeofence = GlobalHelpers.chunkArray(
        processedInformation.arrToResolveGeoreference,
        CLICKGO_MAX_BUNDLE_VALUE
      )
      const forLoop = async () => {
        for (let index = 0; index < chunkGeofence.length; index++) {
          const element = chunkGeofence[index]
          try {
            //Resolvemos georeferencia
            const response = await GlobalHelpers.getBundleLocation(element)
            for (
              let indexResponse = 0;
              indexResponse < response.length;
              indexResponse++
            ) {
              // Con la georeferencia resuelta se procede a completar los datos faltantes en el array que va al excel, y ademas se almacena en el state
              const responseElement = response[indexResponse]
              //Para guardar en el state la georeferencia obtengo el id del objeto en cuestión
              const id = items[responseElement.index].id
              const payload = { id, geoReference: responseElement }

              this.setGeoReference(payload)
              //Completo para la exportación a excel la información faltante
              processedInformation.rowsForExcel[
                responseElement.index
              ].location = responseElement.formatted_address
            }
          } catch (err) {
            // En caso que un lote no pueda resolverse intentaremos en una pasada resolver de manera individual
            for (let i = 0; i < element.length; i++) {
              const item = []
              item.push(element[i])
              try {
                const response = await GlobalHelpers.getBundleLocation(item)
                // Con la georeferencia resuelta se procede a completar los datos faltantes en el array que va al excel, y ademas se almacena en el state
                const responseElement = response[0]
                //Para guardar en el state la georeferencia obtengo el id del objeto en cuestión
                const id = items[responseElement.index].id
                const payload = { id, geoReference: responseElement }

                this.setGeoReference(payload)
                //Completo para la exportación a excel la información faltante
                processedInformation.rowsForExcel[
                  responseElement.index
                ].location = responseElement.formatted_address
              } catch (error) {
                // esta posición tiene que indicar mensaje de error
                const index = element[i].index
                processedInformation.rowsForExcel[index].location = i18n.t(
                  'live_tracking.map.trail_grid.export_excel.geocoding_not_resolved'
                )
              }
            }
            this.loadingExport = false
            this.showMenuExport = false
          }
        }
      }
      forLoop().then(() => {
        const rowsForExcelLength = processedInformation.rowsForExcel.length
        for (let index = 0; index < rowsForExcelLength; index++) {
          // Se añaden rows al worksheet
          ws.addRow(processedInformation.rowsForExcel[index])
        }
        wb.xlsx.writeBuffer().then(data => {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          saveAs(blob, name + '-' + new Date().valueOf() + '.xlsx')
        })
        this.loadingExport = false
        this.showMenuExport = false
      })
    },
    toggleEventsColumn(val) {
      this.toggleShowEvents(val)
    }
  },*/},
  computed: {
    ...mapGetters('trailState', [
      'getThingTrailLocations',
      'getThing',
      'dataLoading'
    ]),
    ...mapState('trailState', ['showEvents']),
    loadingDataTable() {
      return this.dataLoading
    },
    locations() {
      const items = this.getThingTrailLocations
      let newItems = []
      if (items.length > 0) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.locationLenght = items.length
        newItems = items.slice(this.start, this.perPage + this.start)
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.loadingDataTable = false
      }
      return newItems
    },
    btnSize() {
      const size = { xs: 'x-small', sm: 'small', lg: 'small', xl: '' }[
        this.$vuetify.breakpoint.name
      ]
      return size ? { [size]: true } : {}
    },
    dateStartComputed() {
      const date = this.startDate
        ? dayjs(this.startDate).format('DD/MM/YYYY')
        : ''
      return date
    },
    dateEndComputed() {
      const date = this.endDate ? dayjs(this.endDate).format('DD/MM/YYYY') : ''
      return date
    }
  },
  created() {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    dayjs.extend(localizedFormat)
    dayjs.extend(updateLocale)
    dayjs.updateLocale('en', {
      weekdaysShort: 'Dom_Lun_Mar_Mie_Jue_Vie_Sab'.split('_'),
      monthsShort: 'Ene_Feb_Mar_Abr_May_Jun_Jul_Ago_Sep_Oct_Nov_Dic'.split('_')
    })
    // this.headers = Object.values(this.headersMap).filter(
    //   obj => obj.value !== 'events'
    // )
  }
}
</script>

<style>
.trail-grid__controls {
  border-bottom: 1px solid var(--pale-sky-blue);
}
</style>
